/**
 * bundle-tiers.js — shared "spend & save" tier engine.
 *
 * Single source of truth for the whole-order spend-reward ladder, consumed by:
 *   - the collection Bundle Builder Bar (assets/bundle-builder.js)
 *   - the PDP Bundle block               (assets/product-bundle.js)
 *   - the mini-cart rewards bar          (assets/bundle-cart-rewards.js)
 *
 * The ladder is authored once in Theme Settings › Bundle rewards and emitted in
 * CENTS by snippets/bundle-tiers-data.liquid as a data-tiers JSON string. This
 * module only does the maths + copy — it never touches the DOM and never applies
 * a discount (the real saving is a Shopify Automatic Discount set up in admin).
 *
 * Tier shape: { min: <cents>, percent: <int|null>, free_shipping: <bool> }
 * Exposed as window.theme.bundleTiers.
 */
(function () {
  window.theme = window.theme || {};

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const formatString = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const fraction = parts[1] ? decimal + parts[1] : '';
      return dollars + fraction;
    }

    let value = '';
    const match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // Like formatMoney but trims a trailing .00 / ,00 (e.g. "$60.00" -> "$60").
  function formatMoneyShort(cents, format) {
    let out = formatMoney(cents, format);
    if (out.slice(-3) === '.00' || out.slice(-3) === ',00') out = out.slice(0, -3);
    return out;
  }

  /**
   * Thresholds are authored in the shop's currency, but every cart total we compare
   * them against (cart.items_subtotal_price, /cart.js) is in the buyer's presentment
   * currency. Convert with the rate Shopify injects on every page — the same one the
   * theme's own free-shipping bar uses (theme.dev.js, `cartFreeLimitShipping`).
   *
   * Rounds up to a whole unit so a tier never unlocks before the shop-currency
   * threshold is genuinely met — the discount itself is applied by Shopify against
   * the shop-currency minimum, so under-promising is the safe direction to err.
   */
  function toPresentment(shopSubunits) {
    const currency = window.Shopify && window.Shopify.currency;
    const rate = currency ? parseFloat(currency.rate) : NaN;
    if (!rate || isNaN(rate) || rate === 1) return shopSubunits;
    return Math.ceil((shopSubunits * rate) / 100) * 100;
  }

  // Normalise a raw data-tiers value (JSON string or array) into sorted tiers.
  function parseTiers(raw) {
    let tiers;
    try {
      tiers = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      tiers = [];
    }
    if (!Array.isArray(tiers)) tiers = [];
    return tiers
      .filter((t) => t && typeof t.min === 'number' && !isNaN(t.min))
      .map((t) => ({
        min: toPresentment(t.min),
        percent: (t.percent === null || t.percent === undefined || t.percent === '') ? null : Number(t.percent),
        free_shipping: t.free_shipping === true || t.free_shipping === 'true'
      }))
      .sort((a, b) => a.min - b.min);
  }

  // Human reward label, e.g. "10% off", "20% off + free shipping", "free shipping".
  function describeReward(tier) {
    if (!tier) return '';
    const parts = [];
    if (tier.percent) parts.push(tier.percent + '% off');
    if (tier.free_shipping) parts.push('free shipping');
    return parts.join(' + ');
  }

  // Given a subtotal in cents, work out the active tier and the next one.
  function resolve(subtotalCents, tiers) {
    tiers = Array.isArray(tiers) ? tiers.slice().sort((a, b) => a.min - b.min) : [];
    let current = null;
    let next = null;
    for (let i = 0; i < tiers.length; i++) {
      if (subtotalCents >= tiers[i].min) {
        current = tiers[i];
      } else {
        next = tiers[i];
        break;
      }
    }
    return {
      current,
      next,
      atTop: !!(current && !next),
      hasReward: !!current,
      currentPercent: current && current.percent ? current.percent : 0,
      currentFreeShipping: !!(current && current.free_shipping),
      remainingCents: next ? Math.max(0, next.min - subtotalCents) : 0,
      tiers
    };
  }

  // Replace {token} or [token] placeholders from a tokens map.
  function fillTemplate(str, tokens) {
    if (!str) return '';
    tokens = tokens || {};
    return String(str).replace(/\{(\w+)\}|\[(\w+)\]/g, (match, a, b) => {
      const key = a || b;
      return Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : match;
    });
  }

  window.theme.bundleTiers = {
    formatMoney,
    formatMoneyShort,
    toPresentment,
    parseTiers,
    describeReward,
    resolve,
    fillTemplate
  };
})();
